import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { STATUS_LABELS, STATUS_COLORS, PRIORITET_LABELS } from '../lib/format';
import type { TicketStatus, Prioritet } from '../lib/types';
import { Avatar } from '../components/ticket/Avatar';
import { api } from '../lib/api';

interface Overview {
  totalTickets: number;
  byStatus: Record<string, number>;
  byPriority: Record<string, number>;
  unassignedCount: number;
  avgResponseTimeMs: number | null;
  ticketsToday: number;
  ticketsThisWeek: number;
  resolvedThisWeek: number;
}

interface AgentStat {
  id: string;
  navn: string;
  epost: string;
  avatarUrl?: string | null;
  openTickets: number;
  resolvedThisWeek: number;
  avgResponseTimeMs: number | null;
}

interface TrendPoint {
  date: string;
  created: number;
  resolved: number;
}

export default function DashboardPage() {
  const { data: overview, isLoading: loadingOverview } = useQuery<Overview>({
    queryKey: ['stats-overview'],
    queryFn: () => api.get<Overview>('/stats/overview'),
    refetchInterval: 60_000,
  });

  const { data: agents } = useQuery<AgentStat[]>({
    queryKey: ['stats-agents'],
    queryFn: () => api.get<AgentStat[]>('/stats/agents'),
  });

  const { data: trend } = useQuery<TrendPoint[]>({
    queryKey: ['stats-trend'],
    queryFn: () => api.get<TrendPoint[]>('/stats/trend?days=14'),
  });

  if (loadingOverview) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-6 md:px-6">
        <div className="mb-6 h-8 w-40 skeleton" />
        <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="card h-24 p-4">
              <div className="h-3 w-20 skeleton" />
              <div className="mt-2 h-7 w-12 skeleton" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!overview) {
    return <div className="p-6 text-slate-400">Kunne ikke laste statistikk.</div>;
  }

  const openCount = (overview.byStatus['NY'] || 0) + (overview.byStatus['UNDER_ARBEID'] || 0);
  const waitingCount = (overview.byStatus['VENTER_KUNDE'] || 0) + (overview.byStatus['VENTER_INTERNT'] || 0);
  const resolvedCount = (overview.byStatus['LOST'] || 0) + (overview.byStatus['LUKKET'] || 0);

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 md:px-6">
      {/* Header */}
      <div className="mb-6 animate-fade-in-down">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Dashboard</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Oversikt over saker og ytelse</p>
      </div>

      {/* KPI-kort – gradient + animasjon */}
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard label="Åpne saker" value={openCount} gradient="from-blue-500 to-cyan-500" delay={0} />
        <KpiCard label="Venter" value={waitingCount} gradient="from-purple-500 to-violet-500" delay={0.05} />
        <KpiCard label="Løst/lukket" value={resolvedCount} gradient="from-green-500 to-emerald-500" delay={0.1} />
        <KpiCard label="Ufordelte" value={overview.unassignedCount} gradient="from-orange-500 to-amber-500" delay={0.15} />
      </div>

      {/* Sekundær-KPI */}
      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3">
        <MiniStat label="Opprettet i dag" value={overview.ticketsToday} />
        <MiniStat label="Opprettet denne uken" value={overview.ticketsThisWeek} />
        <MiniStat label="Løst denne uken" value={overview.resolvedThisWeek} highlight />
      </div>

      {/* Responstid – fremhevet kort */}
      {overview.avgResponseTimeMs !== null && (
        <div className="mb-6 card animate-fade-in-up overflow-hidden p-0">
          <div className="flex items-center gap-4 p-5">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-accent-500 shadow-glow">
              <svg className="h-6 w-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <div>
              <div className="text-sm text-slate-500 dark:text-slate-400">Gjennomsnittlig responstid (30 dager)</div>
              <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                {formatDuration(overview.avgResponseTimeMs)}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {/* Status-fordeling */}
        <div className="card animate-fade-in-up p-5" style={{ animationDelay: '0.2s' }}>
          <h2 className="mb-4 text-sm font-semibold text-slate-700 dark:text-slate-200">Status-fordeling</h2>
          <div className="space-y-3">
            {Object.entries(overview.byStatus).map(([status, count]) => (
              <div key={status} className="flex items-center gap-3">
                <span className={clsx('chip shrink-0', STATUS_COLORS[status as TicketStatus])}>
                  {STATUS_LABELS[status as TicketStatus] || status}
                </span>
                <div className="flex flex-1 items-center gap-2">
                  <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                    <div
                      className="h-full rounded-full bg-gradient-brand transition-all duration-500 ease-out-expo"
                      style={{ width: `${overview.totalTickets > 0 ? (count / overview.totalTickets) * 100 : 0}%` }}
                    />
                  </div>
                  <span className="w-8 text-right text-sm font-semibold text-slate-700 tabular-nums dark:text-slate-200">{count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Prioritet-fordeling */}
        <div className="card animate-fade-in-up p-5" style={{ animationDelay: '0.25s' }}>
          <h2 className="mb-4 text-sm font-semibold text-slate-700 dark:text-slate-200">Prioritet-fordeling</h2>
          <div className="space-y-3">
            {Object.entries(overview.byPriority).map(([prio, count]) => (
              <div key={prio} className="flex items-center gap-3">
                <span className="w-16 shrink-0 text-sm text-slate-600 dark:text-slate-300">
                  {PRIORITET_LABELS[prio as Prioritet] || prio}
                </span>
                <div className="flex flex-1 items-center gap-2">
                  <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                    <div
                      className="h-full rounded-full bg-slate-400 transition-all duration-500 ease-out-expo dark:bg-slate-500"
                      style={{ width: `${overview.totalTickets > 0 ? (count / overview.totalTickets) * 100 : 0}%` }}
                    />
                  </div>
                  <span className="w-8 text-right text-sm font-semibold text-slate-700 tabular-nums dark:text-slate-200">{count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Trend-graf */}
      {trend && trend.length > 0 && (
        <div className="mt-4 card animate-fade-in-up p-5" style={{ animationDelay: '0.3s' }}>
          <h2 className="mb-4 text-sm font-semibold text-slate-700 dark:text-slate-200">Trend (14 dager)</h2>
          <div className="flex items-end gap-1" style={{ height: '140px' }}>
            {trend.map((point) => {
              const max = Math.max(...trend.map((t) => Math.max(t.created, t.resolved)), 1);
              return (
                <div key={point.date} className="group flex flex-1 flex-col items-center gap-1.5">
                  <div className="flex w-full items-end justify-center gap-0.5" style={{ height: '110px' }}>
                    <div
                      className="w-1/2 rounded-t bg-gradient-to-t from-brand-600 to-brand-400 transition-all duration-300 hover:from-brand-500 hover:to-brand-300"
                      style={{ height: `${(point.created / max) * 100}%`, minHeight: '2px' }}
                      title={`Opprettet: ${point.created}`}
                    />
                    <div
                      className="w-1/2 rounded-t bg-gradient-to-t from-green-600 to-green-400 transition-all duration-300 hover:from-green-500 hover:to-green-300"
                      style={{ height: `${(point.resolved / max) * 100}%`, minHeight: '2px' }}
                      title={`Løst: ${point.resolved}`}
                    />
                  </div>
                  <span className="text-[10px] text-slate-400">
                    {new Date(point.date).getDate()}/{new Date(point.date).getMonth() + 1}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="mt-3 flex justify-center gap-6 text-xs text-slate-500 dark:text-slate-400">
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded bg-gradient-to-t from-brand-600 to-brand-400" /> Opprettet
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded bg-gradient-to-t from-green-600 to-green-400" /> Løst
            </span>
          </div>
        </div>
      )}

      {/* Agent-stats */}
      {agents && agents.length > 0 && (
        <div className="mt-4 card animate-fade-in-up overflow-hidden" style={{ animationDelay: '0.35s' }}>
          <h2 className="border-b border-slate-100 px-5 py-4 text-sm font-semibold text-slate-700 dark:border-slate-800 dark:text-slate-200">
            Agent-statistikk
          </h2>
          <div className="divide-y divide-slate-50 dark:divide-slate-800/50">
            {agents.map((agent) => (
              <div key={agent.id} className="flex items-center gap-3 px-5 py-4 transition-colors hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                <Avatar navn={agent.navn} avatarUrl={agent.avatarUrl} size="md" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-slate-800 dark:text-slate-100">{agent.navn}</div>
                  <div className="text-xs text-slate-400">{agent.epost}</div>
                </div>
                <div className="flex gap-6 text-center">
                  <div>
                    <div className="text-xl font-bold text-slate-800 tabular-nums dark:text-slate-100">{agent.openTickets}</div>
                    <div className="text-xs text-slate-400">Åpne</div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-green-600 tabular-nums dark:text-green-400">{agent.resolvedThisWeek}</div>
                    <div className="text-xs text-slate-400">Løst (uke)</div>
                  </div>
                  {agent.avgResponseTimeMs !== null && (
                    <div>
                      <div className="text-xl font-bold text-slate-800 tabular-nums dark:text-slate-100">
                        {formatDuration(agent.avgResponseTimeMs)}
                      </div>
                      <div className="text-xs text-slate-400">Resp.tid</div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Hjelpe-komponenter ----------
function KpiCard({ label, value, gradient, delay }: { label: string; value: number; gradient: string; delay: number }) {
  return (
    <div
      className="card-hover animate-fade-in-up relative overflow-hidden p-5"
      style={{ animationDelay: `${delay}s` }}
    >
      {/* Gradient glow i hjørnet */}
      <div className={clsx('pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-gradient-to-br opacity-20 blur-2xl', gradient)} />
      <div className="relative">
        <div className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</div>
        <div className={clsx('mt-2 bg-gradient-to-br bg-clip-text text-3xl font-bold text-transparent tabular-nums', gradient)}>
          {value}
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div className="card p-4">
      <div className="text-xs text-slate-500 dark:text-slate-400">{label}</div>
      <div className={clsx(
        'mt-1 text-xl font-bold tabular-nums',
        highlight ? 'text-green-600 dark:text-green-400' : 'text-slate-700 dark:text-slate-200',
      )}>
        {value}
      </div>
    </div>
  );
}

function formatDuration(ms: number): string {
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)} min`;
  if (ms < 86_400_000) return `${(ms / 3_600_000).toFixed(1)} t`;
  return `${(ms / 86_400_000).toFixed(1)} d`;
}
